<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <component :is="layout">
      <router-view  :key="$route.fullPath"/>
    </component>
    <!-- <vue-fab
      v-if="user"
      main-btn-color="#3599DB"
    >
      <fab-item
        v-for="(item, idx) in getFabItems"
        :idx="idx"
        :title="item.title"
        :color="item.color"
        :icon="item.icon"
        @clickItem="clickFab(item)"
      />
    </vue-fab> -->

    <vue-progress-bar />

    <fab
      v-if="user"
      position="bottom-right"
      bg-color="#28c76f"
      :actions="getFabItems"
      @credit="clickFab('credit list')"
      @report="clickFab('report detailed')"
    />
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";

import { useWindowSize, useCssVar } from "@vueuse/core";

import store from "@/store";
import Fab from "vue-fab";
import bus from "./bus.js";

const CryptoJS = require("crypto-js");

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () =>
  import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");
export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    fab: Fab,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    },

    user() {
      return this.$store.state.app.user;
    },

    //Items to show in the floating bar on mobile UI
    getFabItems() {
      const items = [];

      if (this.$can("credit list")) {
        items.push({
          name: "credit",
          icon: "attach_money",
        });
      }

      if (this.$can("report detailed")) {
        items.push({
          name: "report",
          icon: "analytics",
        });
      }

      return items;
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      "primary",
      "secondary",
      "success",
      "info",
      "warning",
      "danger",
      "light",
      "dark",
    ];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement
      ).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement
        ).value.slice(0, -2)
      );
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },

  mounted() {
  //Start tawk chat
  this.startTawkChat()
  //After 10 seconds , update tawk user name
    setTimeout(this.updateTawkUser, 10000)

    // Get details like permissions and balance
    this.getData();

    // Subscribe to pusher websocket
    this.subscribe();

    // On login update the user name on tawk
    bus.$on("login-success", () => {
      this.updateTawkUser();
    });

    // Progress on loading bar finishes after mounting
    this.$Progress.finish();
    // this.$i18n.locale = 'en'
  },

  created() {
    this.$store.commit('appConfig/UPDATE_LAYOUT_TYPE','horizontal')
    //  [App.vue specific] When App.vue is first loaded start the progress bar
    this.$Progress.start();
    //  hook the progress bar to start before we move router-view
    this.$router.beforeEach((to, from, next) => {
      //  does the page we want to go to have a meta.progress object
      if (to.meta.progress !== undefined) {
        const meta = to.meta.progress;
        // parse meta tags
        this.$Progress.parseMeta(meta);
      }
      //  start the progress bar
      this.$Progress.start();
      //  continue to next page
      next();
    });
    //  hook the progress bar to finish after we've finished moving router-view
    this.$router.afterEach((to, from) => {
      //  finish the progress bar
      this.$Progress.finish();
    });
  },
  methods: {
    startTawkChat(){
        try {
            this.$Tawk.$startChat();
        }
        catch (e){
          //console.log(e)
        }
    },
    updateTawkUser() {
      try {
        if (this.user) {
          let hash = CryptoJS.HmacSHA256(
            this.user.email,
            "6127f96ad6e7610a49b23253"
          );
          hash = CryptoJS.enc.Base64.stringify(hash);

          const visitor = {
            name: this.user.name,
            email: this.user.email,
            hash,
          };
          this.$Tawk.$updateChatUser(visitor);
        }
      } catch (e) {}
    },
    clickFab(route) {
      this.$router.push({ name: route });
    },
    getData() {
      this.$store.dispatch("app/getModules");
      this.$store.dispatch("app/getUserPermissions");
      this.$store.dispatch("app/getBalance");
    },
    subscribe() {
      this.pusher.subscribe("module");
      this.pusher.bind("module.build", () => {
        window.location.reload();
      });
      this.pusher.bind("module.upsert", () => {
        this.$store.dispatch("app/getModules");
      });
      this.pusher.bind("module.announcement", () => {
        bus.$emit("announcement");
      });

      if (this.$store.state.app.user) {
        this.pusher.subscribe(`user${this.$store.state.app.user.id}`);
        this.pusher.bind("credit.change", () => {
          this.$store.dispatch("app/getBalance");
        });
      }
    },
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade",
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, (val) => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses,
    };
  },
};
</script>

<style>
.brand-logo img {
  max-width: 100px !important;
}

.required {
  color: red;
}

.card-body {
  display: block;
  width: 100%;
  overflow-x: auto;
}

#loading-bg {
  z-index: 1;
}

.collapse-item-fixed-width {
  width: 150px;
}

.fab-wrapper {
  right: 22px !important;
  bottom: 11vh !important;
  display: none;
}

@media (max-width: 767px) {
  .fab-wrapper {
    display: block;
  }
}
</style>

<style lang="scss">
</style>
